import Image from 'next/image';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import type { ReactNode } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { CONNECTOR_ICONS } from '@/components/Chat/connectors/icons';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import {
  BLOOM_BANNER,
  BRAND,
  CAST,
  type CastMember,
  CUSTOMER,
  FLOAT_CARD,
  Mention,
  Portrait,
  type Status,
  useEmployeesT,
  useHireHref,
  WINDOW,
} from './kit';

export default function EmployeesHero({
  roleCount,
  appCount,
}: {
  roleCount: number;
  /** Apps an employee can connect to, rounded down to a tens figure. */
  appCount: number;
}) {
  const { t } = useEmployeesT();
  const hireHref = useHireHref();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-12 sm:pt-20 lg:px-8'>
        <a
          href='#film'
          className={clsxm(
            styles.enter,
            'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
            tone.body,
          )}
        >
          <span className='flex-none rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
            {t('New')}
          </span>
          <span className='truncate'>{t('Watch the launch film')}</span>
          <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
        </a>

        <div className='mt-8 grid gap-x-14 gap-y-8 lg:grid-cols-12 lg:items-end'>
          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              styles.heroTitle,
              'text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:col-span-7 lg:text-[5.25rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            <Trans
              i18nKey='hero-headline'
              ns='ai-employees'
              defaultTrans='Hire AI employees that work <0>as one team</0>.'
              components={[
                <span
                  key='0'
                  className='text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </h1>

          <div
            className={clsxm(styles.enter, 'lg:col-span-5 lg:pb-2')}
            style={delay(160)}
          >
            <p
              className={clsxm(
                'text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              {t(
                'Pick from {{count}} ready-made roles or describe your own. Give one brief, and your team splits the work, tracks every task and asks before anything goes out.',
                { count: roleCount },
              )}
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-3'>
              <Link href={hireHref} className={buttonPrimary}>
                {t('Hire your team free')}
                <ArrowGlyph />
              </Link>
              <a href='#roles' className={buttonSecondary}>
                {t('Meet the roles')}
              </a>
            </div>
            <ul
              className={clsxm(
                'mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-medium',
                tone.quiet,
              )}
            >
              {[
                t('Free plan included'),
                t('Asks before anything is sent'),
                t('{{count}}+ apps', { count: appCount }),
              ].map((item) => (
                <li key={item} className='flex items-center gap-1.5'>
                  <CheckGlyph className='h-4 w-4 text-emerald-600 dark:text-emerald-400' />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8'>
        <Stage
          variant='indigo'
          halftoneAt='0% 100%'
          className={clsxm(
            styles.enter,
            'px-3 py-6 sm:px-8 sm:py-10 lg:px-10 lg:py-12',
          )}
          style={delay(240)}
        >
          <p className='sr-only'>
            {t(
              'Example: you ask the Chief of Staff for a product launch. He hands the numbers to the analyst, the banner to the designer and the email to the email specialist. Their work arrives in the group chat, every task is tracked, and the email waits for your approval.',
            )}
          </p>
          <div
            aria-hidden='true'
            className='relative mx-auto grid max-w-6xl items-start gap-5 lg:grid-cols-12 lg:gap-0'
          >
            <div className='min-w-0 lg:col-span-8 lg:pe-2'>
              <div className={styles.enter} style={delay(360)}>
                <TeamWindow />
              </div>
            </div>
            {/* Overlaps only the window's side padding, never its content.
                On desktop the cards spread down the window's height, so the
                approval card sits beside Ella's draft. */}
            <div className='grid min-w-0 gap-5 sm:grid-cols-2 lg:col-span-4 lg:-ms-3 lg:flex lg:flex-col lg:justify-between lg:gap-6 lg:self-stretch lg:py-14'>
              <div
                className={clsxm(styles.enter, 'min-w-0')}
                style={delay(2700)}
              >
                <TasksCard />
              </div>
              <div
                className={clsxm(
                  styles.enter,
                  'hidden min-w-0 lg:ms-10 lg:block',
                )}
                style={delay(3000)}
              >
                <RoutineCard />
              </div>
              <div
                className={clsxm(styles.enter, 'min-w-0 lg:mb-20 lg:ms-6')}
                style={delay(3300)}
              >
                <ApprovalCard />
              </div>
            </div>
          </div>
        </Stage>
      </div>
    </header>
  );
}

type Direct = { member: CastMember; status: Status };

/** The team messenger: sidebar, the launch group chat and its composer. */
function TeamWindow() {
  const { t } = useEmployeesT();
  const team = [CAST.atlas, CAST.nova, CAST.mira, CAST.ella];
  const directs: Direct[] = [
    { member: CAST.atlas, status: 'done' },
    { member: CAST.nova, status: 'done' },
    { member: CAST.mira, status: 'done' },
    { member: CAST.ella, status: 'waiting' },
    { member: CAST.maya, status: 'idle' },
  ];

  return (
    <div className={clsxm(WINDOW, 'md:grid md:grid-cols-[190px_1fr]')}>
      <aside className='hidden border-e border-white/[0.06] bg-white/[0.02] p-3 md:block'>
        <p className='px-2 pb-3 pt-1 text-[13px] font-semibold text-white/90'>
          {t('AI Employees')}
        </p>
        <SidebarRow label={t('Approvals')} badge='1' badgeTone='amber' />
        <SidebarRow label={t('Tasks')} badge='2' />
        <p className='mt-4 px-2 pb-1.5 text-[11px] font-medium text-white/40'>
          {t('Group chats')}
        </p>
        <SidebarRow label={`# ${t('Spring launch')}`} active />
        <SidebarRow label={`# ${t('Support inbox')}`} />
        <p className='mt-4 px-2 pb-1.5 text-[11px] font-medium text-white/40'>
          {t('Direct messages')}
        </p>
        <ul className='space-y-0.5'>
          {directs.map(({ member, status }) => (
            <li
              key={member.name}
              className='flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] text-white/75'
            >
              <Portrait
                src={member.photo}
                size={22}
                status={status}
                ringClass='ring-[#15151d]'
              />
              <span className='truncate'>{member.name}</span>
            </li>
          ))}
        </ul>
      </aside>

      <div className='flex min-w-0 flex-col'>
        <div className='flex items-center gap-3 border-b border-white/[0.06] px-4 py-3.5 sm:px-5'>
          <span className='text-lg font-semibold text-white/40'>#</span>
          <div className='min-w-0 flex-1'>
            <p className='truncate text-[15px] font-semibold'>
              {t('Spring launch')}
            </p>
            <p className='truncate text-xs text-white/45'>
              {BRAND} · {t('Lead: {{name}}', { name: CAST.atlas.name })}
            </p>
          </div>
          <span className='flex items-center -space-x-1.5 rtl:space-x-reverse'>
            {team.map((member) => (
              <Portrait
                key={member.name}
                src={member.photo}
                size={24}
                className='rounded-full ring-2 ring-[#121219]'
              />
            ))}
          </span>
        </div>

        <div className='space-y-5 px-4 py-5 text-[14px] leading-relaxed sm:px-5'>
          <Message
            owner
            name={t('You')}
            time='9:41'
            at={600}
            text={
              <Trans
                i18nKey='hero-brief'
                ns='ai-employees'
                defaultTrans='<0>Atlas</0> we launch Bloom on Monday. We need an email, a banner and last spring’s numbers.'
                components={[<Mention key='0' />]}
              />
            }
          />
          <Message
            member={CAST.atlas}
            lead
            time='9:41'
            at={1100}
            text={
              <Trans
                i18nKey='hero-delegation'
                ns='ai-employees'
                defaultTrans='On it. <0>Nova</0> pull last spring’s numbers. <1>Mira</1> design the banner. <2>Ella</2> write the launch email.'
                components={[
                  <Mention key='0' />,
                  <Mention key='1' />,
                  <Mention key='2' />,
                ]}
              />
            }
          />
          <Message
            member={CAST.nova}
            time='9:43'
            at={1600}
            text={t(
              'Last spring we had 1,240 orders. Launch week was the best.',
            )}
          >
            <OrdersChart />
          </Message>
          <Message
            member={CAST.mira}
            time='9:44'
            at={2100}
            text={t('Banner’s ready, sized for the site and Instagram.')}
          >
            <div className='relative mt-2.5 aspect-[16/9] w-full max-w-[300px] overflow-hidden rounded-xl ring-1 ring-white/10'>
              <Image
                src={BLOOM_BANNER}
                alt=''
                fill
                unoptimized
                sizes='300px'
                className='object-cover'
              />
            </div>
          </Message>
          <Message
            member={CAST.ella}
            time='9:45'
            at={2500}
            text={t('Draft is in. Subject: “Meet Bloom, our spring scent.”')}
          >
            <div className='mt-2.5 flex max-w-[340px] items-center gap-3 rounded-xl bg-white/[0.05] px-3.5 py-3 ring-1 ring-inset ring-white/[0.08]'>
              <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white'>
                {CONNECTOR_ICONS.gmail('h-5 w-5')}
              </span>
              <span className='min-w-0 flex-1'>
                <span className='block truncate text-[13px] font-semibold'>
                  {t('Launch email · draft')}
                </span>
                <span className='block truncate text-xs text-white/50'>
                  {t('Waiting for your OK')}
                </span>
              </span>
              <span className='h-2 w-2 flex-none rounded-full bg-amber-400' />
            </div>
          </Message>
        </div>

        <div className='mt-auto px-4 pb-4 sm:px-5'>
          <div className='flex items-center gap-2 rounded-2xl bg-white/[0.06] py-2 pe-2 ps-4 ring-1 ring-inset ring-white/[0.06]'>
            <span className='flex-1 truncate text-sm text-white/40'>
              {t('Message the team · @ to mention')}
            </span>
            <span className='flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-white'>
              <ArrowGlyph className='h-3.5 w-3.5 -rotate-90 rtl:rotate-90' />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarRow({
  label,
  active = false,
  badge,
  badgeTone = 'indigo',
}: {
  label: string;
  active?: boolean;
  badge?: string;
  badgeTone?: 'indigo' | 'amber';
}) {
  return (
    <p
      className={clsxm(
        'flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px]',
        active ? 'bg-white/[0.08] font-medium text-white' : 'text-white/65',
      )}
    >
      <span className='min-w-0 flex-1 truncate'>{label}</span>
      {badge ? (
        <span
          className={clsxm(
            'rounded-full px-1.5 text-[11px] font-semibold leading-[18px]',
            badgeTone === 'amber'
              ? 'bg-amber-400 text-[#2b1d00]'
              : 'bg-primary-500 text-white',
          )}
        >
          {badge}
        </span>
      ) : null}
    </p>
  );
}

function Message({
  member,
  owner = false,
  lead = false,
  name,
  time,
  at,
  text,
  children,
}: {
  member?: CastMember;
  owner?: boolean;
  lead?: boolean;
  name?: string;
  time: string;
  /** Entrance delay in ms. */
  at: number;
  text: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useEmployeesT();
  return (
    <div
      className={clsxm(styles.bubble, styles.bubbleStart, 'flex gap-3')}
      style={delay(at)}
    >
      {member ? (
        <Portrait src={member.photo} size={32} className='mt-0.5' />
      ) : (
        <span className='mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#e7e5ff] text-[12px] font-semibold text-primary-700'>
          {owner ? t('You').slice(0, 1) : '?'}
        </span>
      )}
      <div className='min-w-0 flex-1'>
        <p className='flex flex-wrap items-baseline gap-x-2 text-[13px]'>
          <span className='font-semibold text-white'>
            {member ? member.name : name}
          </span>
          {member ? (
            <span className={clsxm('text-xs font-medium', member.accent)}>
              {t(member.role)}
            </span>
          ) : null}
          {lead ? (
            <span className='rounded bg-primary-500/25 px-1.5 text-[10px] font-semibold uppercase leading-4 tracking-wide text-primary-100'>
              {t('Lead')}
            </span>
          ) : null}
          <span className='text-[11px] text-white/35' dir='ltr'>
            {time}
          </span>
        </p>
        <p className='mt-0.5 text-white/85'>{text}</p>
        {children}
      </div>
    </div>
  );
}

const ORDER_BARS = [38, 52, 46, 61, 58, 70, 100];

function OrdersChart() {
  const { t, lang } = useEmployeesT();
  // Arabic defaults to Arabic-Indic digits; the page copy uses Latin ones.
  const orders = new Intl.NumberFormat(
    lang === 'ar' ? 'ar-u-nu-latn' : lang,
  ).format(1240);
  return (
    <div className='mt-2.5 flex max-w-[340px] items-end gap-4 rounded-xl bg-white/[0.05] px-4 py-3 ring-1 ring-inset ring-white/[0.08]'>
      <div className='flex-none'>
        <p className='text-2xl font-semibold leading-none tracking-tight'>
          {orders}
        </p>
        <p className='mt-1.5 text-[11px] text-white/50'>
          {t('orders last spring')}
        </p>
      </div>
      <div className='flex h-12 flex-1 items-end gap-1.5'>
        {ORDER_BARS.map((height, index) => (
          <span
            key={index}
            className={clsxm(
              'flex-1 rounded-[3px]',
              index === ORDER_BARS.length - 1
                ? 'bg-primary-400'
                : 'bg-white/[0.16]',
            )}
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function TasksCard() {
  const { t } = useEmployeesT();
  const tasks: {
    number: number;
    title: string;
    member: CastMember;
    status: 'review' | 'progress';
  }[] = [
    {
      number: 14,
      title: t('Spring launch banner'),
      member: CAST.mira,
      status: 'review',
    },
    {
      number: 15,
      title: t('Launch email'),
      member: CAST.ella,
      status: 'review',
    },
    {
      number: 16,
      title: t('Launch-week sales report'),
      member: CAST.nova,
      status: 'progress',
    },
  ];

  return (
    <div className='rounded-2xl bg-[#0d0c1f] p-4 text-start text-white shadow-[0_30px_70px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10'>
      <p className='flex items-center justify-between px-1 text-xs font-semibold text-white/60'>
        <span>{t('Tasks')}</span>
        <span className='font-medium text-white/40'>
          # {t('Spring launch')}
        </span>
      </p>
      <ul className='mt-3 space-y-1'>
        {tasks.map(({ number, title, member, status }) => (
          <li
            key={number}
            className='flex items-center gap-3 rounded-xl px-1.5 py-1.5'
          >
            <Portrait src={member.photo} size={28} />
            <span className='min-w-0 flex-1'>
              <span className='block truncate text-[13px] font-medium'>
                {title}
              </span>
              <span className='block text-[11px] text-white/45' dir='ltr'>
                #{number}
              </span>
            </span>
            <span
              className={clsxm(
                'flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold',
                status === 'review'
                  ? 'bg-amber-400/15 text-amber-300'
                  : 'bg-primary-400/15 text-primary-200',
              )}
            >
              {status === 'review' ? t('In review') : t('In progress')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoutineCard() {
  const { t } = useEmployeesT();
  return (
    <div className='flex items-center gap-3 rounded-2xl bg-white/[0.12] p-3.5 text-start text-white ring-1 ring-inset ring-white/20'>
      <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white/15'>
        <svg
          viewBox='0 0 20 20'
          fill='currentColor'
          className='h-4 w-4'
          aria-hidden='true'
        >
          <path
            fillRule='evenodd'
            d='M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .199.079.39.22.53l3 3a.75.75 0 101.06-1.06l-2.78-2.78V5z'
            clipRule='evenodd'
          />
        </svg>
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-[13px] font-semibold'>
          {CAST.nova.name} · {t('Daily business brief')}
        </span>
        <span className='block truncate text-xs text-white/65'>
          {t('Next run: tomorrow, 8:00')}
        </span>
      </span>
    </div>
  );
}

function ApprovalCard() {
  const { t } = useEmployeesT();
  return (
    <div className={clsxm(FLOAT_CARD, 'p-4')}>
      <p className='flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-300'>
        <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
        {t('{{name}} needs your OK', { name: CAST.ella.name })}
      </p>
      <div className='mt-3 flex items-start gap-3'>
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#f3f3f7] dark:bg-white'>
          {CONNECTOR_ICONS.gmail('h-5 w-5')}
        </span>
        <div className='min-w-0'>
          <p className='text-[14px] font-semibold leading-snug'>
            {t('Send an email to {{name}}', { name: CUSTOMER.name })}
          </p>
          <p className={clsxm('mt-0.5 truncate text-xs', tone.quiet)}>
            {CUSTOMER.company} · {t('Bloom is here: spring stock for Luma')}
          </p>
        </div>
      </div>
      <div className='mt-4 grid grid-cols-3 gap-2 text-[13px] font-semibold'>
        <span className='rounded-full py-2 text-center ring-1 ring-inset ring-black/10 dark:ring-white/15'>
          {t('Reject')}
        </span>
        <span className='rounded-full py-2 text-center ring-1 ring-inset ring-black/10 dark:ring-white/15'>
          {t('Edit')}
        </span>
        <span className='rounded-full bg-primary-600 py-2 text-center text-white'>
          {t('Approve')}
        </span>
      </div>
    </div>
  );
}
